/**
 * /ndt-inspection/technicians — every technician's NDT certifications, one row
 * per credential (fieldiq-api#1134 `GET /ndt-inspection/technicians/certs`).
 *
 * STATUS IS THE API'S. valid / due soon / expired / no expiry is computed
 * server-side against the tenant's today and expiry warn window; this screen
 * colours it and never derives it.
 *
 * FILTERING IS THE SERVER'S for status, method and technician — they are query
 * params the API reads.
 *
 * THE COUNTS ARE A TALLY OF THE UNFILTERED LIST, honest only because no NDT
 * endpoint returns a count and the list is unpaginated (every live cert is
 * answered). If it is ever paginated, the counts must come from the API.
 */
import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, BadgeCheck, Clock, Plus, Users } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { ndtPersonName, useNdtCatalog, useNdtCerts, useNdtPeople } from '../../hooks/useNdtInspection';
import { queryLoadFailure } from '../../lib/load-failure';
import { canAdminNdt, daysHint, ndtExpirySkin, ndtLevelLabel, NDT_EXPIRY_SKIN } from '../../lib/ndt-inspection';
import { NDT_EXPIRY_STATUSES, type NdtTechnicianCert } from '../../types/ndt-inspection';
import { CertDialog } from './NdtDialogs';
import { NDT_INPUT, NdtBadge, NdtChip, NdtChipRow, NdtEmpty, NdtPanel, NdtRegister, ndtDay, type NdtColumn } from './NdtBits';

const isStatus = (v: string | null): v is (typeof NDT_EXPIRY_STATUSES)[number] =>
  !!v && (NDT_EXPIRY_STATUSES as readonly string[]).includes(v);

export default function TechniciansScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const method = searchParams.get('method') ?? '';
  const userId = searchParams.get('user') ?? '';
  const [adding, setAdding] = useState(false);

  const params = useMemo(() => ({ status, method, userId }), [status, method, userId]);
  const filtered = !!(status || method || userId);
  const listQuery = useNdtCerts(params);
  const allQuery = useNdtCerts();
  const catalog = useNdtCatalog();
  const people = useNdtPeople();

  const certs = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (c: NdtTechnicianCert) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const countsLoading = allQuery.isLoading;
  const canAdd = canAdminNdt(user?.role);

  const setParam = (key: string, next: string) => {
    const p = new URLSearchParams(searchParams);
    if (next) p.set(key, next);
    else p.delete(key);
    setSearchParams(p, { replace: true });
  };

  const columns: NdtColumn<NdtTechnicianCert>[] = [
    {
      key: 'tech',
      header: 'Technician',
      render: (c) => <span className="font-extrabold text-white">{c.userName || '—'}</span>,
    },
    { key: 'method', header: 'Method', render: (c) => c.methodLabel || c.method || '—' },
    { key: 'level', header: 'Level', render: (c) => ndtLevelLabel(c.level) },
    { key: 'scheme', header: 'Scheme', render: (c) => c.schemeLabel || c.scheme || '—' },
    { key: 'number', header: 'Certificate #', render: (c) => <span className="font-mono">{c.certificateNumber || '—'}</span> },
    {
      key: 'expires',
      header: 'Expires',
      render: (c) => (
        <>
          {ndtDay(c.expiresOn)}
          {c.status !== 'none' && daysHint(c.daysLeft, 'ago') && <p className="mt-0.5 text-xs text-fieldiq-muted">{daysHint(c.daysLeft, 'ago')}</p>}
        </>
      ),
    },
    { key: 'status', header: 'Status', render: (c) => <NdtBadge skin={ndtExpirySkin(c.status)} value={c.status} /> },
  ];

  return (
    <div data-testid="ndt-technicians-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="NDT inspection"
        title="Technicians & Certs"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canAdd ? (
            <button type="button" onClick={() => setAdding(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              Add certification
            </button>
          ) : undefined
        }
        stats={[
          { icon: <Users className="size-4" aria-hidden="true" />, label: 'Certs', sub: 'On file', tone: 'neutral', loading: countsLoading, value: countOf(() => true) },
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Due soon', sub: 'Warn window', tone: 'accent', loading: countsLoading, value: countOf((c) => c.status === 'due_soon') },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Expired', sub: 'Past expiry', tone: 'accent', loading: countsLoading, value: countOf((c) => c.status === 'expired') },
          { icon: <BadgeCheck className="size-4" aria-hidden="true" />, label: 'Valid', sub: 'Current', tone: 'success', loading: countsLoading, value: countOf((c) => c.status === 'valid') },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <NdtChipRow label="Filter by status">
            <NdtChip label="All" active={!status} onClick={() => setParam('status', '')} count={countsLoading ? undefined : countOf(() => true)} />
            {NDT_EXPIRY_STATUSES.map((s) => (
              <NdtChip
                key={s}
                label={NDT_EXPIRY_SKIN[s].label}
                active={status === s}
                onClick={() => setParam('status', s)}
                count={countsLoading ? undefined : countOf((c) => c.status === s)}
              />
            ))}
          </NdtChipRow>
          <div className="grid gap-3 sm:grid-cols-2 lg:max-w-[48rem]">
            <label className="block">
              <span className="sr-only">Method</span>
              <select className={NDT_INPUT} value={method} onChange={(e) => setParam('method', e.target.value)} aria-label="Method">
                <option value="">All methods</option>
                {(catalog.data?.data?.methods ?? []).map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="sr-only">Technician</span>
              <select className={NDT_INPUT} value={userId} onChange={(e) => setParam('user', e.target.value)} aria-label="Technician">
                <option value="">All technicians</option>
                {(people.data ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {ndtPersonName(u)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <NdtPanel
            eyebrow="Qualification register"
            title="Certifications"
            testId="ndt-cert-register"
            footer={
              !listQuery.isLoading && !queryLoadFailure(listQuery) && certs.length > 0
                ? filtered
                  ? `Showing ${certs.length} of ${countOf(() => true)} certifications`
                  : `${certs.length} certifications`
                : undefined
            }
          >
            <NdtRegister
              query={listQuery}
              rows={certs}
              columns={columns}
              rail={(c) => ndtExpirySkin(c.status).rail}
              onOpen={(c) => navigate(`/ndt-inspection/technicians/certs/${c.id}`)}
              subject="technician certifications"
              testId="ndt-cert"
              empty={
                filtered ? (
                  <NdtEmpty>No certification matches these filters.</NdtEmpty>
                ) : (
                  <NdtEmpty>No technician certifications are on file.</NdtEmpty>
                )
              }
              card={(c) => (
                <>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-extrabold text-white">{c.userName || '—'}</p>
                      <p className="mt-0.5 text-xs text-fieldiq-muted">
                        {c.methodLabel || c.method} · {ndtLevelLabel(c.level)}
                      </p>
                    </div>
                    <NdtBadge skin={ndtExpirySkin(c.status)} value={c.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-text">Expires {ndtDay(c.expiresOn)}</p>
                </>
              )}
            />
          </NdtPanel>
        </div>
      </section>

      {canAdd && <CertDialog open={adding} onClose={() => setAdding(false)} defaultUserId={userId || undefined} />}
    </div>
  );
}
